import { Customer, Device, isValidDeviceObject, } from '@iotv/datamodel';
import { DevicePoolType, } from '../../types/AppTypes';
import ApiGateway from '../aws/api-gateway/ApiGateway';
const debug = process.env.REACT_APP_DEBUG && false;
const apiRef = 'SystemManagementApi';
const consumerApiRef = 'ConsumerApi';
const addToDevicesInventory = async ({ user, contextCustomer, devices }) => {
    const result = { err: null, data: null };
    if (contextCustomer) {
        const customerClassInstance = new Customer(contextCustomer);
        if (customerClassInstance.canHaveDeviceInventory()) {
            const promises = await Promise.all(devices.map((device) => {
                return ApiGateway.post('/system/Device/addToInventory', { contextUser: user, device, contextCustomer }, apiRef);
            }));
            let successes = 0, fails = 0, errMessage = '';
            promises.forEach((deviceRes) => {
                if (deviceRes.err) {
                    fails++;
                    errMessage += `${fails !== 0 ? ', ' : ''}${deviceRes.err.message}`;
                }
                else {
                    successes++;
                }
            });
            if (fails > 0) {
                const msg = `${successes} devices processed successfully but ${fails} devices were not processed. \n${errMessage}`;
                result.err = new Error(msg);
            }
            else {
                result.data = `${successes} devices processed successfully`;
            }
        }
    }
    return result;
};
const listDevices = async ({ contextRequest, devicePoolType }) => {
    const urlPart = devicePoolType === DevicePoolType.INVENTORY ? 'listInventoryDevices' : devicePoolType === DevicePoolType.ALLOCATION ? 'listAllocatedDevices' : null;
    let response = { err: null, data: null };
    if (urlPart) {
        try {
            response = (await ApiGateway.post(`/system/Device/${urlPart}`, contextRequest, apiRef));
        }
        catch (e) {
            debug && console.log('Error in UM query', e);
        }
    }
    return response;
};
const deleteDevice = async ({ device, contextCustomer, user }) => {
    const response = await ApiGateway.post('/system/Device/deleteDevice', { contextUser: user, contextCustomer, device, devicePoolType: DevicePoolType.INVENTORY }, apiRef);
    return response;
};
const addToDeviceInventory = async ({ device, contextCustomer, user }) => {
    const customerClassInstance = new Customer(contextCustomer);
    let response = { err: null, data: null };
    const deviceInstance = new Device(device);
    const isValid = isValidDeviceObject(device) && deviceInstance.isLoRaLNSCompatible();
    if (!isValid) {
        const invalidKeys = deviceInstance.getInvalidLNSAttributes();
        response.err = new Error(`Device has invalid keys: ${invalidKeys.join(',')}`);
        return response;
    }
    if (customerClassInstance.canHaveDeviceInventory()) {
        response = await ApiGateway.post('/system/Device/addToInventory', { contextUser: user, device, contextCustomer }, apiRef);
        debug && console.log('Add response', response);
    }
    return response;
};
const assignDeviceToCustomer = async ({ device, contextCustomer, assignmentCustomer, contextUser, assignmentCustomerGroup }) => {
    let response = { err: null, data: null };
    response = await ApiGateway.post('/system/Device/assignDeviceToCustoemr', { contextUser, device, contextCustomer, assignmentCustomer, assignmentCustomerGroup }, apiRef);
    debug && console.log('Add response', response);
    return response;
};
const unAllocateDevice = async ({ contextCustomer, contextUser, device, unAssignmentCustomer, billingGroup, thingGroup }) => {
    let response = { err: null, data: null };
    const request = {
        contextUser,
        contextCustomer,
        unAssignmentCustomer,
        device,
    };
    debug && console.log('unAllocateDevice request', request);
    response = await ApiGateway.post(`/system/Device/unassignDeviceFromCustomer`, request, apiRef);
    debug && console.log('unAllocateDevice results', response);
    return response;
};
const getDevice = async ({ contextCustomer, contextUser, device, devicePoolType }) => {
    let response = { err: null, data: null };
    response = await ApiGateway.post('/system/Device/getDevice', { contextCustomer, contextUser, device, devicePoolType }, apiRef);
    debug && console.log('getDeviceQuery query results', response);
    return response;
};
const addDevice = async ({ device, deviceGroup, contextUser, contextCustomer }) => {
    let response = { err: null, data: null };
    const deviceInstance = new Device(device);
    const isValid = isValidDeviceObject(device) && deviceInstance.isLoRaLNSCompatible();
    if (!isValid) {
        const invalidKeys = deviceInstance.getInvalidLNSAttributes();
        response.err = new Error(`Device has invalid keys: ${invalidKeys.join(',')}`);
        return response;
    }
    response = await ApiGateway.post('/system/Device/addDevice', { device, deviceGroup, contextUser, contextCustomer }, apiRef);
    debug && console.log('addDevice response', response);
    return response;
};
const updateDevice = async ({ device, contextCustomer, contextUser }) => {
    let response = { err: null, data: null };
    const updateRequest = { contextUser, contextCustomer, device };
    response = await ApiGateway.post('/system/Device/updateDevice', updateRequest, apiRef);
    debug && console.log('Update response', { updateRequest, response });
    return response;
};
/**
 * Returns the customer from which the device would be allocated if possible, or an error explaining why
 */
const deviceIsAllocatableThroughHierarchy = async ({ device, installCustomer }) => {
    const path = '/device/isAllocatableThroughHierarchy/';
    let response = { err: null, data: null };
    try {
        response = await ApiGateway.post(path, { device, installCustomer }, consumerApiRef);
    }
    catch (e) {
        response.err = e;
    }
    return response;
};
/**
 * Returns the final created edge between the `contextCustomer` and the device provided in the parameters, i.e. the new link, or an error explaining why
 */
const allocateDeviceThroughHierarchy = async ({ contextCustomer, device, contextUser }) => {
    let response = { err: null, data: null };
    const path = '/device/allocateDeviceThroughHierarchy/';
    try {
        response = await ApiGateway.post(path, { device, contextCustomer, contextUser }, consumerApiRef);
    }
    catch (e) {
        response.err = e;
    }
    return response;
};
const getConnectableDevices = async ({ contextCustomer, deviceEUI, deviceSubType, deviceType, possibleDeviceEnabledTypeIds, }) => {
    const res = await ApiGateway.post('/device/getConnectableDevices/', { contextCustomer, deviceEUI, deviceSubType, deviceType, possibleDeviceEnabledTypeIds }, apiRef);
    return res;
};
/**
 * **Device DAO**
 */
export const DeviceDAO = {
    addToDevicesInventory,
    addToDeviceInventory,
    assignDeviceToCustomer,
    unAllocateDevice,
    listDevices,
    addDevice,
    getDevice,
    deleteDevice,
    updateDevice,
    deviceIsAllocatableThroughHierarchy,
    allocateDeviceThroughHierarchy,
    getConnectableDevices,
};
